import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import { MeasurementType, type TrainingFeedback } from "@fitness-app/data-models/entities/Measurement";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { updateWorkoutActivityRate } from "../../traineeActivities/reducer";
import { TRAINEE_MEASUREMENTS_REDUCER_NAME } from "../types";

type Payload = {
  data: TrainingFeedback["data"];
  eventDate: string;
  traineeId: string;
  /** Id of an existing feedback row — when set the row is updated instead of inserted. */
  id?: null | string;
  /**
   * Id for the row created on the insert path. Callers that retry the whole write (react-query
   * `retry`) pass a fixed one so a retry after a lost response updates the same row instead of
   * inserting a second feedback. Ignored when `id` is set.
   */
  insertId?: string | null;
  userId?: string | null;
  activityId?: string | null;
  onSuccess?: ({ feedbackId, activityId }: { feedbackId: string; activityId: string }) => void | Promise<void>;
};

export const upsertTrainingFeedback = createAsyncThunk<TrainingFeedback, Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_MEASUREMENTS_REDUCER_NAME}/upsertTrainingFeedback`,
  async (payload, { rejectWithValue, dispatch, extra: { db, auth, analytics, errorTrackingService } }) => {
    const user = await getLoggedUser(auth);

    if (payload.id) {
      const { data, error } = await db
        .from("measurement")
        .update({
          data: payload.data,
          updatedAt: new Date().toISOString(),
        })
        .eq("id", payload.id)
        .select("*")
        .maybeSingle<TrainingFeedback>();

      if (error) {
        errorTrackingService?.recordError(error, "updateTrainingFeedback");
        return rejectWithValue(error.message);
      }

      if (data) {
        analytics.track("update_training_rate", {
          rate: payload.data.rate,
          status: payload.data.trainingStatus,
        });

        dispatch(updateWorkoutActivityRate(data));
        return data;
      }

      // No row to update: the activity still carries a `feedbackId` whose measurement was
      // deleted (or RLS hides it). Failing here would leave the client unable to rate the workout
      // at all, so write a fresh row and relink the activity below instead.
    }

    // TRE-1706: the `measurement` RLS policy for a client accepts a row only when `userId` equals
    // `auth.uid()`, so a null owner is rejected with 42501. Callers read the id from UI state that
    // can legitimately be empty — on web `useUserId()` returns "" until the session hydrates, on
    // mobile the trainee profile carries a null `userId` when the record is not linked to an
    // account — so fall back to the authenticated user instead of writing a null RLS refuses.
    // Every caller of this thunk is a client surface; a trainer writing for a client passes the
    // client's id explicitly, which still wins here.
    const ownerId = payload.userId || user.id;

    const measurement: TrainingFeedback = {
      data: payload.data,
      type: MeasurementType.FeedbackAboutTraining,
      eventDate: payload.eventDate,
      eventTimestamp: dayjs(payload.eventDate, "YYYY-MM-DD").hour(12).toISOString(),
      traineeId: payload.traineeId,
      creator: user.id === ownerId ? AuthorTypeEnum.Client : AuthorTypeEnum.Trainer,
      id: payload.insertId || generateUniqId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: ownerId,
    };

    // Idempotent write keyed on the client-generated `id` (measurement_pkey).
    // A flaky connection can cause the same POST to be delivered twice (the row commits
    // but the response is lost, then the request is retried), which an `insert` would
    // reject with "duplicate key value violates unique constraint measurement_pkey".
    // `upsert` turns the duplicate into a no-op update of the same row instead of crashing.
    // The same holds for a caller-level retry that reuses `insertId`.
    const { error, data } = await db
      .from("measurement")
      .upsert(measurement, { onConflict: "id" })
      .select("*")
      .single<TrainingFeedback>();

    if (error) {
      errorTrackingService?.recordError(error, "addTrainingFeedback");
      return rejectWithValue(error.message);
    }

    if (payload.activityId) {
      if (payload.onSuccess) {
        await payload.onSuccess({
          feedbackId: measurement.id,
          activityId: payload.activityId,
        });
      } else {
        await db.from("client_workout_activity").update({ feedbackId: measurement.id }).eq("id", payload.activityId);
      }
    }

    analytics.track("add_training_rate", {
      rate: payload.data.rate,
      status: payload.data.trainingStatus,
    });
    dispatch(updateWorkoutActivityRate(data));

    return data;
  },
);
